import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NavHashLink } from "react-router-hash-link";
import { DashboardText } from "../common/Dashboard";
import { AdminIcon } from "../../sections/AdminDashboard/shared/adminUx";
import ProfileComponent from "../common/Profile/ProfileComponent";
import useAuth from "../../Hooks/useAuth";
import { useDispatch } from "react-redux";
import useAdminAccess from "../../Hooks/useAdminAccess";
import { resetAdminSession } from "../../Redux/Slice/adminAccessSlice";

const NavIconWrap = ({ children }) => (
  <AdminIcon className="text-inherit">{children}</AdminIcon>
);

const normalizePath = (path) => (path || "/").replace(/\/+$/, "") || "/";

const pathMatches = (pathname, link) => {
  if (!link || link === "/admin/logout") return false;
  const current = normalizePath(pathname);
  const target = normalizePath(link);
  if (target === "/admin/dashboard") return current === target;
  return current === target || current.startsWith(`${target}/`);
};

const subItemActive = (sub, pathname) => {
  if (pathMatches(pathname, sub.link)) return true;
  return sub.subMenu?.some((nested) => pathMatches(pathname, nested.link)) ?? false;
};

const sectionActive = (item, pathname) => {
  if (item.subMenu?.some((sub) => subItemActive(sub, pathname))) return true;
  return pathMatches(pathname, item.link);
};

export default function AdminSidebar() {
  const [open, setOpen] = useState(null);
  const [activeSubId, setActiveSubId] = useState(null);
  const [nestedSubOpenId, setNestedSubOpenId] = useState(null);
  const { auth, setAuth } = useAuth();
  const dispatch = useDispatch();
  const { activeAccount, isOwnerView, getVisibleSidebarLinks } = useAdminAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const handleLogout = () => {
    dispatch(resetAdminSession());
    localStorage.removeItem("LoginSession");
    setAuth({
      user: null,
      roles: null,
      accessToken: "",
    });
    navigate("/admin/logout");
  };

  const toggleHandler = (index) => {
    setOpen((prev) => (prev === index ? null : index));
  };

  // Admin sidebar menu items – FINAL order and structure
  const adminSidebarLinks = [
    {
      linkName: "Dashboard",
      link: "/admin/dashboard",
      icon: <i className="fi fi-rr-dashboard"></i>,
      iconSelected: <i className="fi fi-rr-dashboard text-primaryblue"></i>,
    },
    {
      linkName: "Accounts",
      link: "/admin/users/customers",
      icon: <i className="fi fi-rr-users"></i>,
      iconSelected: <i className="fi fi-rr-users text-primaryblue"></i>,
      subMenu: [
        { id: "accounts-customers", linkName: "Customers", link: "/admin/users/customers" },
        { id: "accounts-businesses", linkName: "Businesses", link: "/admin/users/businesses" },
      ],
    },
    {
      linkName: "Activity",
      link: "/admin/posts/all",
      icon: <i className="fi fi-rr-pulse"></i>,
      iconSelected: <i className="fi fi-rr-pulse text-primaryblue"></i>,
      subMenu: [
        { id: "activity-posts", linkName: "Posts", link: "/admin/posts/all" },
        { id: "activity-reviews", linkName: "Reviews", link: "/admin/reviews/all" },
        { id: "activity-messages", linkName: "Messages", link: "/admin/communication/all" },
        { id: "activity-quotes", linkName: "Quotes", link: "/admin/quotes/all" },
      ],
    },
    {
      linkName: "Cases",
      link: "/admin/moderation/queue",
      icon: <i className="fi fi-rr-folder"></i>,
      iconSelected: <i className="fi fi-rr-folder text-primaryblue"></i>,
      subMenu: [
        {
          id: "cases-moderation",
          linkName: "Moderation",
          link: "/admin/moderation/queue",
          subMenu: [
            { id: "moderation-queue", linkName: "Moderation Queue", link: "/admin/moderation/queue" },
            { id: "moderation-archive", linkName: "Moderation Archive", link: "/admin/moderation/violations" },
          ],
        },
        {
          id: "cases-compliance",
          linkName: "Compliance",
          link: "/admin/verification/queue",
          subMenu: [
            { id: "compliance-queue", linkName: "Compliance Queue", link: "/admin/verification/queue" },
            { id: "compliance-archive", linkName: "Compliance Archive", link: "/admin/verification/archive" },
          ],
        },
        {
          id: "cases-fraud-security",
          linkName: "Fraud & Security",
          link: "/admin/fraud/alerts",
          subMenu: [
            { id: "fraud-security-queue", linkName: "Fraud & Security Queue", link: "/admin/fraud/alerts" },
            { id: "fraud-security-archive", linkName: "Fraud & Security Archive", link: "/admin/fraud/archive" },
          ],
        },
        { id: "cases-support-tickets", linkName: "Support Tickets", link: "/admin/support/tickets" },
        { id: "cases-appeals", linkName: "Appeals", link: "/admin/appeals" },
      ],
    },
    {
      linkName: "Billing",
      link: "/admin/billing/transactions",
      icon: <i className="fi fi-rr-credit-card"></i>,
      iconSelected: <i className="fi fi-rr-credit-card text-primaryblue"></i>,
      subMenu: [
        {
          id: "billing-subscriptions",
          linkName: "Billing & Subscriptions",
          link: "/admin/billing/transactions",
        },
      ],
    },
    {
      linkName: "Insights",
      link: "/admin/analytics/platform",
      icon: <i className="fi fi-rr-chart-pie"></i>,
      iconSelected: <i className="fi fi-rr-chart-pie text-primaryblue"></i>,
      subMenu: [
        { id: "insights-analytics", linkName: "Analytics", link: "/admin/analytics/platform" },
        { id: "insights-reports", linkName: "Reports", link: "/admin/reports" },
      ],
    },
    {
      linkName: "Content",
      link: "/admin/content",
      icon: <i className="fi fi-rr-document"></i>,
      iconSelected: <i className="fi fi-rr-document text-primaryblue"></i>,
      subMenu: [
        { id: "blog-articles", linkName: "Blog & Articles", link: "/admin/content/blog" },
        { id: "faqs", linkName: "FAQs", link: "/admin/content/faqs" },
        { id: "knowledge-base", linkName: "Knowledge Base", link: "/admin/support/kb" },
      ],
    },
    {
      linkName: "Registers",
      link: "/admin/trade-categories",
      icon: <i className="fi fi-rr-layers"></i>,
      iconSelected: <i className="fi fi-rr-layers text-primaryblue"></i>,
      subMenu: [
        { id: "registers-trade-categories", linkName: "Trade Categories", link: "/admin/trade-categories" },
        { id: "registers-keywords", linkName: "Keywords", link: "/admin/keywords" },
        { id: "registers-form-inventory", linkName: "Form Inventory", link: "/admin/form-inventory" },
        {
          id: "registers-licences-badges-tickets",
          linkName: "Licences, Badges & Tickets",
          link: "/admin/licences-badges-trade-tickets",
        },
        { id: "registers-pricing-plans", linkName: "Pricing Plans", link: "/admin/pricing-plans" },
      ],
    },
    {
      linkName: "Configuration",
      link: "/admin/configuration/validation",
      icon: <i className="fi fi-rr-settings-sliders"></i>,
      iconSelected: <i className="fi fi-rr-settings-sliders text-primaryblue"></i>,
      subMenu: [
        {
          id: "config-validation",
          linkName: "Validation",
          link: "/admin/configuration/validation",
        },
        {
          id: "config-workflow",
          linkName: "Workflow",
          link: "/admin/configuration/workflow",
        },
        {
          id: "config-automation",
          linkName: "Automation",
          link: "/admin/configuration/automation",
        },
        {
          id: "config-lifecycle",
          linkName: "Lifecycle",
          link: "/admin/configuration/lifecycle",
        },
        {
          id: "config-retention",
          linkName: "Retention",
          link: "/admin/configuration/retention",
        },
        {
          id: "config-plans-access",
          linkName: "Pricing Plans",
          link: "/admin/configuration/plans",
        },
        {
          id: "config-content-control",
          linkName: "Content Control",
          link: "/admin/configuration/content-control",
        },
        {
          id: "config-flag",
          linkName: "Flag",
          link: "/admin/configuration/flag",
        },
        {
          id: "config-publishing",
          linkName: "Publishing",
          link: "/admin/configuration/publishing",
        },

        {
          id: "config-search-matching",
          linkName: "Search & Matching",
          link: "/admin/configuration/search-matching",
        },
        {
          id: "config-quote-matching-distribution",
          linkName: "Quote Matching & Distribution",
          link: "/admin/configuration/quote-matching-distribution",
        },
        {
          id: "config-appeals",
          linkName: "Appeals",
          link: "/admin/configuration/appeals",
        },
        {
          id: "config-notifications",
          linkName: "Notifications",
          link: "/admin/configuration/notifications",
        },
        {
          id: "config-case-actions",
          linkName: "Case Actions",
          link: "/admin/configuration/case-actions",
        },
        {
          id: "config-support-ticket",
          linkName: "Support Ticket",
          link: "/admin/configuration/support-ticket",
        },
        {
          id: "config-limits",
          linkName: "Limits & Thresholds",
          link: "/admin/configuration/limits",
        },
        {
          id: "config-permissions-access",
          linkName: "Permissions & Access",
          link: "/admin/configuration/permissions-access",
        },
      ],
    },
    {
      linkName: "Admin",
      link: "/admin/settings/team-members",
      icon: <i className="fi fi-rr-user"></i>,
      iconSelected: <i className="fi fi-rr-user text-primaryblue"></i>,
      subMenu: [
        { id: "admin-team-members", linkName: "Team Members", link: "/admin/settings/team-members" },
        { id: "admin-settings", linkName: "Settings", link: "/admin/settings/general" },
        { id: "admin-integrations", linkName: "Integrations", link: "/admin/integrations" },
        { id: "admin-change-history", linkName: "Change History", link: "/admin/settings/history" },
        { id: "admin-system-logs", linkName: "System Logs", link: "/admin/system-logs" },
      ],
    },
    {
      linkName: "Log Out",
      link: "/admin/logout",
      icon: <i className="fi fi-rr-sign-out-alt"></i>,
      iconSelected: <i className="fi fi-rr-sign-out-alt text-red-600"></i>,
      onClick: handleLogout,
      isLogout: true,
    },
  ];

  const visibleSidebarLinks = useMemo(
    () => getVisibleSidebarLinks(adminSidebarLinks),
    [getVisibleSidebarLinks],
  );

  const sidebarProfileData = useMemo(() => {
    if (activeAccount && !isOwnerView) {
      const personName =
        activeAccount.name ||
        `${activeAccount.firstName || ''} ${activeAccount.lastName || ''}`.trim() ||
        'Team Member';
      return {
        firstName: activeAccount.firstName,
        lastName: activeAccount.lastName,
        businessName: personName,
        profilePicture: activeAccount.profilePicture,
        startDate: activeAccount.startDate || activeAccount.memberSince || null,
        jobTitle: activeAccount.roleLabel,
      };
    }
    const personName = 'Saim Sahi';
    return {
      firstName: 'Saim',
      lastName: 'Sahi',
      businessName: personName,
      profilePicture: auth?.profilePicture || auth?.user?.profilePicture,
      startDate: auth?.startDate || auth?.user?.startDate || '2024-01-01',
      jobTitle: isOwnerView ? 'Owner' : null,
    };
  }, [activeAccount, auth, isOwnerView]);

  const sidebarIdentityLabel = useMemo(() => {
    if (activeAccount && !isOwnerView) {
      return activeAccount.sidebarLabel || activeAccount.team || activeAccount.roleLabel || 'Team Member';
    }
    return 'Owner';
  }, [activeAccount, isOwnerView]);

  useEffect(() => {
    const expandedIndex = visibleSidebarLinks.findIndex((item) => sectionActive(item, pathname));
    if (expandedIndex >= 0 && visibleSidebarLinks[expandedIndex]?.subMenu) {
      setOpen(expandedIndex);
      const activeSub = visibleSidebarLinks[expandedIndex].subMenu.find((sub) =>
        subItemActive(sub, pathname),
      );
      if (activeSub) {
        setActiveSubId(activeSub.id);
        if (activeSub.subMenu?.some((nested) => pathMatches(pathname, nested.link))) {
          setNestedSubOpenId(activeSub.id);
          const activeNested = activeSub.subMenu.find((nested) => pathMatches(pathname, nested.link));
          if (activeNested) setActiveSubId(activeNested.id);
        }
      }
    }
  }, [pathname, visibleSidebarLinks]);

  return (
    <>
      <div
        id="admin-sidebar"
        className={`lg:w-[230px] xl:w-[230px] 2xl:w-[230px] h-auto bg-white rounded-xl`}
      >
        <ProfileComponent
          heading={sidebarProfileData.businessName || "Admin Panel"}
          userData={sidebarProfileData}
          profileCompleteion={false}
          actions={true}
          dashboardActions={false}
        />
        <div className="mt-2">
          <DashboardText variant="supporting" as="p" className="text-mediumgray capitalize">
            {sidebarIdentityLabel}
          </DashboardText>
        </div>
        <div className="overflow-y-auto text-offblack mt-5 pt-5 border-t border-t-mediumgray">
          <ul>
            <li>
              {visibleSidebarLinks?.map((e, index) => {
                const itemActive = sectionActive(e, pathname);
                if (e.subMenu) {
                  const expanded = open === index;
                  return (
                    <li key={e.linkName || index}>
                      <button
                        type="button"
                        className={
                          expanded || itemActive
                            ? "flex items-center justify-between px-0 py-3 text-primaryblue w-full"
                            : "flex items-center justify-between px-0 py-3 hover:text-primaryblue w-full"
                        }
                        aria-expanded={expanded}
                        onClick={() => {
                          toggleHandler(index);
                        }}
                      >
                        <span className="flex items-center gap-3">
                          <NavIconWrap>{itemActive ? e.iconSelected : e.icon}</NavIconWrap>
                          <span
                            className={
                              expanded || itemActive
                                ? "text-primaryblue whitespace-nowrap"
                                : "text-offblack hover:text-primaryblue whitespace-nowrap"
                            }
                          >
                            <DashboardText variant="nav" as="span" className="capitalize">
                              {e.linkName}
                            </DashboardText>
                          </span>
                        </span>
                        <AdminIcon className="text-offblack leading-none shrink-0">
                          <i className="fi fi-rs-angle-down" aria-hidden />
                        </AdminIcon>
                      </button>
                      {open === index && (
                        <ul
                          id="dropdown-example"
                          className="transition duration-150 ease-in-out py-2 space-y-2 flex justify-center flex-col w-full pl-3"
                        >
                          {e.subMenu.map((sub) => (
                            <li key={sub.id}>
                              {sub.subMenu ? (
                                // Item with submenu (3rd level)
                                <div>
                                  <button
                                    className="flex items-center justify-between w-full pl-5 pr-0 py-2 text-left hover:text-primaryblue text-offblack"
                                    onClick={() => {
                                      setNestedSubOpenId((prev) => (prev === sub.id ? null : sub.id));
                                    }}
                                  >
                                    <DashboardText variant="nav" as="span" className="capitalize">
                                      {sub.linkName}
                                    </DashboardText>
                                    <AdminIcon className="text-offblack leading-none shrink-0">
                                      <i className="fi fi-rs-angle-down" aria-hidden />
                                    </AdminIcon>
                                  </button>
                                  {nestedSubOpenId === sub.id && (
                                    <ul className="transition duration-150 ease-in-out py-2 space-y-2 flex justify-center flex-col w-full pl-5">
                                      {sub.subMenu.map((subSub) => (
                                        <li key={subSub.id}>
                                          <NavHashLink
                                            to={subSub.link}
                                            onClick={() => setActiveSubId(subSub.id)}
                                            className={`flex items-center gap-3 hover:text-primaryblue w-full px-5 py-2 ${
                                              pathMatches(pathname, subSub.link)
                                                ? "text-primaryblue"
                                                : "text-offblack"
                                            }`}
                                          >
                                            <DashboardText variant="nav" as="span" className="capitalize">
                                              {subSub.linkName}
                                            </DashboardText>
                                          </NavHashLink>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              ) : (
                                // Regular submenu item (2nd level)
                                <NavHashLink
                                  to={sub.link}
                                  onClick={() => setActiveSubId(sub.id)}
                                  className={`flex items-center gap-3 hover:text-primaryblue w-full px-5 py-2 ${
                                    pathMatches(pathname, sub.link) ? "text-primaryblue" : "text-offblack"
                                  }`}
                                >
                                  <DashboardText variant="nav" as="span" className="capitalize">
                                    {sub.linkName}
                                  </DashboardText>
                                  {typeof sub.badge !== "undefined" && (
                                    <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] min-w-5 h-5 px-1 ml-1 leading-none">
                                      {sub.badge}
                                    </span>
                                  )}
                                </NavHashLink>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  // Handle logout item with button instead of Link
                  if (e.isLogout) {
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          if (e.onClick) {
                            e.onClick();
                          }
                        }}
                        className="flex items-center px-0 py-3 gap-3 text-offblack hover:text-red-600 w-full transition-colors"
                      >
                        <NavIconWrap>{e.icon}</NavIconWrap>
                        <span className="flex items-center gap-2">
                          <DashboardText variant="nav" as="span" className="capitalize">
                            {e.linkName}
                          </DashboardText>
                        </span>
                      </button>
                    );
                  }
                  // Regular menu item
                  return (
                    <Link
                      key={index}
                      to={e.link}
                      onClick={() => {
                        setOpen(null);
                        if (e.onClick) {
                          e.onClick();
                        }
                      }}
                      className={`flex items-center px-0 py-3 gap-3 hover:text-primaryblue ${
                        pathMatches(pathname, e.link) ? "text-primaryblue" : "text-offblack"
                      }`}
                    >
                      <NavIconWrap>
                        {pathMatches(pathname, e.link) ? e.iconSelected : e.icon}
                      </NavIconWrap>
                      <span className="flex items-center gap-2">
                        <DashboardText variant="nav" as="span" className="capitalize">
                          {e.linkName}
                        </DashboardText>
                        {typeof e.badge !== "undefined" && (
                          <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] min-w-5 h-5 px-1 ml-1 leading-none">
                            {e.badge}
                          </span>
                        )}
                      </span>
                    </Link>
                  );
                }
              })}
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
